import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const ACCENT = '#0f766e';

interface RecentPage {
    path: string;
    label: string;
    icon: string;
    timestamp: number;
}

const PAGE_LABELS: Record<string, { label: string; icon: string }> = {
    '/institution/home': { label: 'Dashboard', icon: 'bx bx-home-alt' },
    '/institution/ehr': { label: 'EHR Search', icon: 'bx bx-file-find' },
    '/institution/ehr/list': { label: 'Browse EHR', icon: 'bx bx-folder-open' },
    '/institution/ehr/new': { label: 'Create EHR', icon: 'bx bx-plus-medical' },
    '/institution/ehr/share': { label: 'Share EHR', icon: 'bx bx-export' },
    '/institution/ehr/shared': { label: 'Shared With Us', icon: 'bx bx-cloud-download' },
    '/institution/ehr/activity': { label: 'EHR Activity', icon: 'bx bx-history' },
    '/institution/patients': { label: 'Patient Center', icon: 'bx bx-group' },
    '/institution/service-center': { label: 'Service Center', icon: 'bx bx-briefcase' },
    '/institution/collaborations': { label: 'Collaborations', icon: 'bx bx-transfer' },
    '/institution/telehealth': { label: 'Telehealth', icon: 'bx bx-video' },
    '/institution/billing': { label: 'Billing', icon: 'bx bx-credit-card' },
    '/institution/resources': { label: 'Resources', icon: 'bx bx-box' },
    '/institution/devices': { label: 'Devices', icon: 'bx bx-chip' },
    '/institution/notifications': { label: 'Notifications', icon: 'bx bx-bell' },
    '/institution/management': { label: 'Management', icon: 'bx bx-buildings' },
    '/institution/settings': { label: 'Settings', icon: 'bx bx-cog' },
};

const STORAGE_KEY = 'uzima_recent_pages';
const MAX_RECENT = 12;

const Launchbar: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [recentPages, setRecentPages] = useState<RecentPage[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: -1, y: -1 });
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const fabRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Load recent pages from localStorage
    useEffect(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) setRecentPages(JSON.parse(saved));
        } catch {}
    }, []);

    // Initialize position (bottom-right)
    useEffect(() => {
        if (position.x === -1) {
            setPosition({ x: window.innerWidth - 70, y: window.innerHeight - 70 });
        }
    }, []);

    // Track page visits
    useEffect(() => {
        const path = location.pathname;
        const info = PAGE_LABELS[path];
        if (!info) return;

        setRecentPages(prev => {
            const filtered = prev.filter(p => p.path !== path);
            const updated = [{ path, label: info.label, icon: info.icon, timestamp: Date.now() }, ...filtered].slice(0, MAX_RECENT);
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch {}
            return updated;
        });
    }, [location.pathname]);

    // Dragging logic
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (!fabRef.current) return;
        const rect = fabRef.current.getBoundingClientRect();
        setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        setIsDragging(true);
        e.preventDefault();
    }, []);

    useEffect(() => {
        if (!isDragging) return;
        const handleMove = (e: MouseEvent) => {
            const x = Math.max(20, Math.min(window.innerWidth - 60, e.clientX - dragOffset.x));
            const y = Math.max(20, Math.min(window.innerHeight - 60, e.clientY - dragOffset.y));
            setPosition({ x, y });
        };
        const handleUp = () => setIsDragging(false);
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
        return () => { window.removeEventListener('mousemove', handleMove); window.removeEventListener('mouseup', handleUp); };
    }, [isDragging, dragOffset]);

    // Touch drag support
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (!fabRef.current) return;
        const rect = fabRef.current.getBoundingClientRect();
        const touch = e.touches[0];
        setDragOffset({ x: touch.clientX - rect.left, y: touch.clientY - rect.top });
        setIsDragging(true);
    }, []);

    useEffect(() => {
        if (!isDragging) return;
        const handleTouchMove = (e: TouchEvent) => {
            const touch = e.touches[0];
            const x = Math.max(20, Math.min(window.innerWidth - 60, touch.clientX - dragOffset.x));
            const y = Math.max(20, Math.min(window.innerHeight - 60, touch.clientY - dragOffset.y));
            setPosition({ x, y });
        };
        const handleTouchEnd = () => setIsDragging(false);
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', handleTouchEnd);
        return () => { window.removeEventListener('touchmove', handleTouchMove); window.removeEventListener('touchend', handleTouchEnd); };
    }, [isDragging, dragOffset]);

    // Close panel on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(e.target as Node) && fabRef.current && !fabRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);

    const handleFabClick = () => {
        if (!isDragging) setIsOpen(!isOpen);
    };

    const goTo = (path: string) => {
        navigate(path);
        setIsOpen(false);
    };

    // Calculate how many items to show based on screen width
    const maxVisible = Math.max(4, Math.min(MAX_RECENT, Math.floor((window.innerWidth - 100) / 80)));
    const visiblePages = recentPages.slice(0, maxVisible);

    // Panel position: opens above or below the FAB
    const panelY = position.y > window.innerHeight / 2 ? 'above' : 'below';
    const panelX = position.x > window.innerWidth / 2 ? 'left' : 'right';

    if (position.x === -1) return null;

    return (
        <>
            {/* FAB Button */}
            <div
                ref={fabRef}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onClick={handleFabClick}
                style={{
                    position: 'fixed',
                    left: position.x,
                    top: position.y,
                    width: 48,
                    height: 48,
                    borderRadius: 16,
                    background: isOpen ? ACCENT : 'rgba(15,23,42,0.85)',
                    backdropFilter: 'blur(12px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    zIndex: 9999,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                    transition: isDragging ? 'none' : 'background 0.2s, transform 0.2s',
                    transform: isOpen ? 'scale(1.05)' : 'scale(1)',
                    userSelect: 'none',
                }}
            >
                <i className={isOpen ? 'bx bx-x' : 'bx bx-rocket'} style={{ fontSize: '1.3rem', color: '#fff', pointerEvents: 'none' }}></i>
                {recentPages.length > 0 && !isOpen && (
                    <div style={{
                        position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: '50%',
                        background: '#ef4444', color: '#fff', fontSize: '0.6rem', fontWeight: 800,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}>
                        {recentPages.length}
                    </div>
                )}
            </div>

            {/* Panel */}
            {isOpen && (
                <div
                    ref={panelRef}
                    style={{
                        position: 'fixed',
                        left: panelX === 'left' ? position.x - 300 : position.x + 60,
                        top: panelY === 'above' ? position.y - Math.min(visiblePages.length * 44 + 70, 420) : position.y + 56,
                        width: 300,
                        maxHeight: 420,
                        borderRadius: 16,
                        background: 'rgba(255,255,255,0.97)',
                        backdropFilter: 'blur(20px)',
                        boxShadow: '0 8px 40px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.05)',
                        zIndex: 9998,
                        overflow: 'hidden',
                        animation: 'launchbarIn 0.2s ease-out',
                    }}
                >
                    {/* Header */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                            <i className="bx bx-rocket me-2" style={{ color: ACCENT }}></i>Quick Launch
                        </div>
                        <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600 }}>Recent pages</span>
                    </div>

                    {/* Pages list */}
                    <div style={{ overflowY: 'auto', maxHeight: 340, padding: '6px 8px' }}>
                        {visiblePages.length === 0 ? (
                            <div style={{ padding: '30px 16px', textAlign: 'center' }}>
                                <i className="bx bx-compass" style={{ fontSize: '1.6rem', color: '#cbd5e1', display: 'block', marginBottom: 8 }}></i>
                                <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>Navigate to pages to see them here</div>
                            </div>
                        ) : (
                            visiblePages.map((page, i) => {
                                const isActive = location.pathname === page.path;
                                return (
                                    <div
                                        key={page.path}
                                        onClick={() => goTo(page.path)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10,
                                            padding: '8px 12px',
                                            borderRadius: 10,
                                            cursor: 'pointer',
                                            background: isActive ? 'rgba(15,118,110,0.08)' : 'transparent',
                                            transition: 'all 0.12s',
                                            marginBottom: 2,
                                        }}
                                        onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,0,0,0.03)'; }}
                                        onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                                    >
                                        <div style={{
                                            width: 32, height: 32, borderRadius: 10,
                                            background: isActive ? 'rgba(15,118,110,0.12)' : 'rgba(15,23,42,0.04)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            <i className={page.icon} style={{ fontSize: '0.95rem', color: isActive ? ACCENT : '#64748b' }}></i>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: isActive ? 700 : 600, fontSize: '0.78rem', color: isActive ? ACCENT : '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {page.label}
                                            </div>
                                            <div style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                                                {new Date(page.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                        {isActive && (
                                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: ACCENT, flexShrink: 0 }}></div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', gap: 6 }}>
                        <button onClick={() => goTo('/institution/home')} style={{ flex: 1, padding: '6px 0', borderRadius: 8, border: 'none', background: 'rgba(15,118,110,0.08)', color: ACCENT, fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer' }}>
                            <i className="bx bx-home-alt me-1"></i>Home
                        </button>
                        <button onClick={() => goTo('/institution/ehr')} style={{ flex: 1, padding: '6px 0', borderRadius: 8, border: 'none', background: 'rgba(15,118,110,0.08)', color: ACCENT, fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer' }}>
                            <i className="bx bx-file-find me-1"></i>EHR
                        </button>
                        <button onClick={() => goTo('/institution/patients')} style={{ flex: 1, padding: '6px 0', borderRadius: 8, border: 'none', background: 'rgba(15,118,110,0.08)', color: ACCENT, fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer' }}>
                            <i className="bx bx-group me-1"></i>Patients
                        </button>
                    </div>
                </div>
            )}

            {/* Animation keyframes */}
            <style>{`
                @keyframes launchbarIn {
                    from { opacity: 0; transform: scale(0.92) translateY(8px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>
        </>
    );
};

export default Launchbar;
